import "assets/css/agentworkspace.css";
import ButtonWithPopover from "components/ButtonWithPopover";
import { activityEventEmitter } from "components/CallGroupDetails/ActivityEventEmitter";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import CloseActivityModal from "components/CallGroupDetails/CloseActivityModal";
import DrugDetails from "components/CallGroupDetails/DrugDetails";
import { Formik } from "formik";
import { AgentWorkspaceConstants } from "lookup/AgentWorkspaceConstants";
import { NOTIFY } from "lookup/SwimConstants";
import { useRef, useEffect, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { showNotification } from "services/NotificationService";
import { useSafeNavigate } from "utils/useSafeNavigate";
import {
  goBackIfCallGroupClosed,
  isLoggedInUserAWAdmin,
  isLoggedInUserAIAdmin,
  isOwnedByLoggedInUser,
  redirectToListingPage
} from "services/AWUtilityService";
import { isStringEmptyOrNull } from "services/UtilityService";
import * as Yup from "yup";
import ExpandActivityHistory from "./ExpandActivityHistory";
import ExpandSwitchTimeline from "./ExpandSwitchTimeline";
import ExpandAiCallResults from "./ExpandAiCallResults";
import ErrorCodes from "lookup/ErrorCodes";
import { getErrorMessage } from "services/UtilityService";

const BottomBar = (props) => {
  const {
    activityDetails,
    areActionsCallable,
    callGroupData,
    data,
    showPrescriberColumn,
  } = props;
  const reopenRef = useRef("reopen");
    const [activityData, setActivityData] = useState([]);
    const [showRawJsonModal, setShowRawJsonModal] = useState(false);
    const navigate = useSafeNavigate();

  const openSwim = () => {
    navigate(`/switch/${activityDetails.wfItem.wfItemId}`);
  };

  const reopenActivitySchema = Yup.object().shape(
    {
      note: Yup.string().required("Required!").max(4096, "Max Length: 4096")
    },
    [
      [
        "note"
      ]
    ]
  );

    useEffect(() => {
    const prescriberUuid =
      activityDetails?.wfItem?.switchInfo?.prescriberInfo
        ?.prescriberInfoUuid;
      if (prescriberUuid) {
        sendAuthenticatedAsyncRequest(
        `/agent-workspace/switches-with-open-call-activities?prescriberInfoUuids=${prescriberUuid}`,
          "GET",
          null,
          (r) => {
            setActivityData(r.data);
          },
          (e) => {
            showNotification({
              title: `Unable to Fetch Relevant Activities!`,
              message:
              getErrorMessage(e.data) || ErrorCodes.SOMETHING_WENT_WRONG,
              position: "tr",
              type: "error",
            });
          }
        );
      }
  }, [activityDetails]);

    const showConfirmationMessage = () => {
        return (
          activityDetails.type !== AgentWorkspaceConstants.CALL &&
          activityDetails.status === AgentWorkspaceConstants.CLOSED &&
          activityDetails.entityType === AgentWorkspaceConstants.PRESCRIBER &&
          activityData?.length > 0
        );
    };


  const submitForm = (originalValues, setSubmitting, closeModal) => {
    // it's best to create a clone because params are passed through reference
    const {
      note,
      activityId
    } = originalValues;
    const event = "REOPEN";
    const prescriberId =
      activityDetails?.wfItem?.switchInfo?.prescriberInfo?.prescriberId;
    activityEventEmitter(
      {
        note,
        version: activityDetails.version,
        data,
        event,
        prescriberId,
      },
      activityId,
      {
        setSubmitting: setSubmitting,
        closeModal: closeModal,
        refreshActivityList: props.updateData,
        refreshActivity: props.refreshActivity,
        resetActivityUponClose: props.resetActivityUponClose,
        goBackIfCallGroupClosed: goBackIfCallGroupClosed.bind(
          this,
          data,
          navigate
        ),
      },
      "SAME_ACTIVITY_ALREADY_OPEN"
    );
  };

  const handleLinkedActivityClick = () => {
    navigate(
      `/agent-workspace/call-group-details/${
        activityDetails.linkedActivity
        ? activityDetails.linkedActivity.callGroupId
        : ""
        }`,
      {
      state: {
        status: activityDetails.linkedActivity
          ? activityDetails.linkedActivity.status
          : "OPEN",
        navigation: AgentWorkspaceConstants.LINKED_ACTIVITY,
      },
  }
    );
  };

  const includeNotifyBadge = activityDetails?.isMemberNotified || false;

  const showAICallButton = () => {
    // Button not displayed under the following circumstances:
    // 1. User is not ROLE_AW_AI_ADMIN
    // 2. Entity type is not PRESCRIBER
    // 3. Activity status is not NEW or OPEN
    // 4. No activities exist in call group
    // 5. There are more than 2 OPEN/NEW activities in the call group
    const validStatuses = ["NEW", "OPEN"];

    // Check all conditions that would hide the button
    if (!isLoggedInUserAIAdmin() ||
        activityDetails.entityType !== AgentWorkspaceConstants.PRESCRIBER ||
        !validStatuses.includes(activityDetails.status) ||
        data.length === 0) {
      return false;
    }

    // Count OPEN/NEW activities - button only shows if 2 or fewer
    const openActivitiesCount = data.filter(
      (activity) => validStatuses.includes(activity.status)
    ).length;

    return openActivitiesCount <= 2;
  };

  const showAiCallResultsButton = () => {
    // Button displayed when:
    // 1. User has ROLE_AW_AI_ADMIN permission
    // 2. Entity type is PRESCRIBER
    // 3. PolyAI call results exist (polyAiCallDetailsList has items)
    return isLoggedInUserAIAdmin() &&
           activityDetails.entityType === AgentWorkspaceConstants.PRESCRIBER &&
           activityDetails.polyAiCallDetailsList &&
           activityDetails.polyAiCallDetailsList.length > 0;
  };

  const isAICallButtonDisabled = () => {
    if (activityDetails.status === AgentWorkspaceConstants.CLOSED) {
      return true;
    }
    const openActivities = data.filter(
      (activity) => activity.status === "NEW" || activity.status === "OPEN"
    );
    if (openActivities.length === 0) {
      return true;
    }
    // Disable reschedule button if attempt count has reached 4
    if (activityDetails.callSid && activityDetails.polyAiCallDetailsList && activityDetails.polyAiCallDetailsList.length >= 4) {
      return true;
    }
    return false;
  };

  const handleAICallButtonClick = () => {
    sendAuthenticatedAsyncRequest(
      `/ai-vendor/call-group/${callGroupData.callGroupId}/polyai/schedule-call`,
      "POST",
      null,
      (r) => {
        showNotification({
          title: "Success",
          message: "Successfully initiated PolyAI.",
          position: "tr",
          type: "success",
        });
        props.updateData();
        props.fetchActivityDetails();
      },
      (e) => {
        showNotification({
          title: "Error",
          message:
            getErrorMessage(e.data) ||
            "An error occurred while initiating PolyAI.",
          position: "tr",
          type: "error",
        });
      }
    );
  };


  return (
    <>
      <Row>
        <Col md={3}>
          {activityDetails.status === "CLOSED" ||
            activityDetails.status === "ON_HOLD" ? (
            <ButtonWithPopover
              placement="top"
              customRef={reopenRef}
              buttonVariant="success"
              buttonText="Reopen Activity"
              buttonIsBlock={true}
              buttonIsDisabled={!isOwnedByLoggedInUser(callGroupData)}
              popoverTitle="Reopen Activity"
              buttonId="reopen-activity"
              buttonClassName="btn-text-white btn-block"
              dataTestId="btn-reopen-activity"
            >
               {showConfirmationMessage() && (
                    <div>
                      <p className="confirmation-text">
                                                     You are about to Close all Open and New Call Activities
                                                     of these switch(es):
                                                   <ul>
                                                     {activityData.map((item) => (
                                                       <li key={item.activityUuid}>
                                                        <b>{item.concatenatedInfo}, {activityDetails?.wfItem?.switchInfo?.prescriberInfo?.prescriberName} </b>
                                                       </li>
                                                     ))}
                                                   </ul>
                                                   </p>
                </div>
                )}

              <p className="confirmation-text">
                Do you want to <b>reopen</b> this activity?
              </p>
              <Formik
                initialValues={{
                  note: "",
                  activityId: activityDetails.activityId,
                  // closeModal: reopenRef.current.handleClose
                }}
                onSubmit={(values, { setSubmitting, setValues }) => {
                  submitForm(values, setSubmitting, reopenRef.current.handleClose);
                }}
                validationSchema={reopenActivitySchema}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  dirty,
                  isValid,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.note}
                            name="note"
                            as="textarea"
                            data-testid="input-reopen-activity-notes"
                          />
                          <Form.Text className="text-small text-danger">
                            {errors.note && touched.note && errors.note}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="align-content-right">
                      <Button
                        id="reopen-activity-btn-save"
                        type="submit"
                        className="left-spacer"
                        variant="success"
                        disabled={isSubmitting || !(dirty && isValid)}
                        block
                        onClick={handleSubmit}
                        data-testid="btn-reopen-activity-submit"
                      >
                        {isSubmitting ? "Updating ... " : "Yes"}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </ButtonWithPopover>
          ) : (
            <CloseActivityModal
              data={data} // passing all ids in call group
              updateData={props.updateData}
              callGroupId={callGroupData.callGroupId}
              activityState={activityDetails.state}
              activityStatus={activityDetails.status}
              activityName={activityDetails.activityName}
              activityType={activityDetails.type}
              entityType={activityDetails.entityType}
              globalSwitchId={activityDetails.wfItem.switchInfo.globalSwitchId}
              isMemberActivityCreatable={
                activityDetails.isMemberActivityCreatable
              }
              prescriberId={activityDetails.wfItem.switchInfo.prescriberInfo.prescriberInfoUuid}
              activityVersion={activityDetails.version}
              prescriberName={activityDetails.wfItem.switchInfo.prescriberInfo.prescriberName}
              pharmacyName={activityDetails.wfItem.switchInfo.pharmaInfo.pharmacyName}
              memberNumber={activityDetails.wfItem.switchInfo.rirMemberInfo.memberNumber}
              isActionCallable={areActionsCallable}
              activityId={activityDetails.activityId}
              callCounter={activityDetails.callCounter}
              resetActivityUponClose={() => props.resetActivityUponClose()}
              refreshActivityList={props.updateData.bind(this)}
              refreshActivity={() => props.refreshActivity()}
              goBackIfCallGroupClosed={goBackIfCallGroupClosed.bind(
                this,
                data,
                navigate
              )}
              goBackIfNoActionTakenOrGroupClose={redirectToListingPage.bind(
                this,
                navigate
              )}
              onActivityEventSuccess={props.onActivityEventSuccess}
            />
          )}
        </Col>
        <Col md={3}>
          {isLoggedInUserAWAdmin() && (
            <Button
              variant="info"
              className="btn-text-white btn-block"
              onClick={() => openSwim()}
              data-testid="btn-activity-open-swim"
            >
              Open in SWIM
            </Button>
          )}
        </Col>
        <Col md={3}>
          <ExpandSwitchTimeline
            activityId={activityDetails.activityId}
            switchData={activityDetails.wfItem}
            isFromAW={true}
            isAWAdmin={isLoggedInUserAWAdmin()}
          />
        </Col>
        <Col md={3}>
          <ExpandActivityHistory activityId={activityDetails.activityId} />
        </Col>
        {showAICallButton() && (
          <Col md={3}>
            <Button
              variant="primary"
              className="btn-text-white btn-block"
              data-testid="btn-schedule-ai-call"
              onClick={handleAICallButtonClick}
              disabled={isAICallButtonDisabled()}
            >
              {activityDetails.callSid ? "Reschedule AI Call" : "Schedule AI Call"}
            </Button>
          </Col>
        )}
        {showAiCallResultsButton() && (
          <Col md={3}>
            <ExpandAiCallResults
              callDetailsList={activityDetails.polyAiCallDetailsList}
              callGroupId={callGroupData?.callGroupId}
            />
          </Col>
        )}
      </Row>
      <Row className="tiny-top-spacer">
        <Col md={6} className="content-centered-col">
          {
            <div className="boxed-div content-align-start">
              <div className="tiny-top-spacer" data-testid="text-health-plan">
                <strong>Health Plan:</strong> {activityDetails.programName}
              </div>
            </div>
          }
          {!isStringEmptyOrNull(activityDetails.state) &&
            activityDetails.state && (
              <div className="boxed-div content-align-start">
                <div data-testid="text-activity-state">
                  <strong>Activity State:</strong> {activityDetails.state}
                </div>
              </div>
            )}
           {!isStringEmptyOrNull(activityDetails.wfItem.state) && !isStringEmptyOrNull(activityDetails.wfItem.status)
             && (
              <div className="boxed-div content-align-start">
                <div data-testid="text-activity-state">
                  <strong>Switch State:</strong> {`${activityDetails.wfItem.status} - ${activityDetails.wfItem.state}`}
                </div>
              </div>
            )}
            {
              <div className="boxed-div content-align-start">
                <div data-testid="text-communication-method">
                  <strong>Communication Method:</strong> {activityDetails.communicationMethod}
                </div>
              </div>
            }
          {activityDetails.isManuallyCreated ? (
            <Badge bg="warning" size="lg" className="ml-1 mt-1 pull-left" data-testid="badge-manually-created">
              {AgentWorkspaceConstants.MANUALLY_CREATED}
            </Badge>
          ) : null}
          {activityDetails.wfItem.callActivityBlockStatus === "BLOCKED" ? (
            <Badge bg="info" size="lg" className="ml-1 mt-1 pull-left">
              {AgentWorkspaceConstants.SWIM_SWITCH}
            </Badge>
          ) : null}
          {activityDetails.linkedActivity ? (
            <Badge onClick={handleLinkedActivityClick.bind(this)} data-testid="badge-linked-activity" bg="success" size="lg" className="link-text ml-1 mt-1 pull-left">
              {activityDetails.isManuallyCreated ? "Go to Parent" : "Go to Child"}
            </Badge>
          ) : null}
          {activityDetails.isFaxUnlinked ? (
            <Badge bg="warning" size="lg" className="ml-1 mt-1 pull-left" data-testid="badge-fax-unlinked">
              {AgentWorkspaceConstants.UNLINKED}
            </Badge>
          ) : null}
          {activityDetails.multipleActivities ? (
            <Badge bg="info" size="lg" className="ml-1 mt-1 pull-left" data-testid="badge-multiple-activities">
              {AgentWorkspaceConstants.MULTIPLE_ACTIVTIES}
            </Badge>
          ) : null}
          {includeNotifyBadge && (
            <Badge
              bg="danger"
              size="lg"
              className="ml-1 mt-1 pull-left"
            >
              {NOTIFY}
            </Badge>
          )}
        </Col>
        <Col md={{ span: 4, offset: 2 }} className="content-centered-col">
          <div className="tiny-top-spacer boxed-div content-align-start">
            <DrugDetails
              sourceDrug={activityDetails.wfItem.switchInfo.claimDrugName}
              targetDrugs={activityDetails.wfItem.switchInfo.targetOptions}
              selectedTarget={activityDetails.wfItem.switchInfo.selectedTarget}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default BottomBar;
